/**
 * Reusable Refresh Button Component
 *
 * A consistent refresh button with loading state and accessibility support.
 * Positioned in top-right corner as per design requirements.
 */

import { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';

interface RefreshButtonProps {
  onRefresh: () => Promise<void> | void;
  variant?: 'primary' | 'secondary' | 'outline-primary' | 'outline-secondary';
  size?: 'sm' | 'lg';
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

export default function RefreshButton({
  onRefresh,
  variant = 'outline-secondary',
  size = 'sm',
  className = '',
  disabled = false,
  ariaLabel = 'Refresh data',
}: RefreshButtonProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (isRefreshing || disabled) return;

    setIsRefreshing(true);
    try {
      await onRefresh();
      toast.success('Data refreshed successfully');
    } catch (error) {
      console.error('Refresh failed:', error);
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleRefresh}
      disabled={disabled || isRefreshing}
      className={className}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {isRefreshing ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-1"
          />
          <span>Refreshing...</span>
        </>
      ) : (
        <>
          <i className="fas fa-sync-alt me-1"></i>
          <span>Refresh</span>
        </>
      )}
    </Button>
  );
}
